import { FETCH_LAYERS_SUCCESS } from '../layers/Types';

import {
    START_SEARCH_REQUEST,
    START_SEARCH_SUCCESS,
    START_SEARCH_FAILURE,
    FETCH_SEARCH_REQUEST,
    FETCH_SEARCH_SUCCESS,
    FETCH_SEARCH_FAILURE,
    PING_SEARCH_REQUEST,
    PING_SEARCH_SUCCESS,
    PING_SEARCH_FAILURE,
    CANCEL_SEARCH_REQUEST,
    CANCEL_SEARCH_SUCCESS,
    CANCEL_SEARCH_FAILURE,
    FETCH_SEARCHES_REQUEST,
    FETCH_SEARCHES_SUCCESS,
    FETCH_SEARCHES_FAILURE,
    EXPORT_SEARCH_REQUEST,
    EXPORT_SEARCH_SUCCESS,
    EXPORT_SEARCH_FAILURE
} from './Types';

import {
    SET_FORM_PROPERTIES,
    SET_AREA_FACET,
    SET_AREA_FACET_VALUE,
    SET_AREA_GEOJSON,
    SET_AREA_CRITERIA,
    SET_AREA_LAYER,
    SET_AREA_CRITERIA_ADVANCED_MODE,
    CLEAR_AREA
} from '../search_area/Types';

import {
    FETCH_CURRENT_PROJECT_SUCCESS, 
    SET_CURRENT_PROJECT_SUCCESS 
} from '../project/Types';

import { SEARCH_REDUCER } from '../ReducerNames';
import areaReducer from '../search_area/Reducer';
import {ISearchAction, ISearchReducerState, SearchArea} from "./searchTypes";
import store from '../Store';
import _ from 'lodash';

const defaultState = {
    isSearching: false,
    isFetching: false,
    isPinging: false,
    isCancelling: false,

    search: null, // The current search running in the background
    isSearchDirty: false,

    searchArea: areaReducer(SEARCH_REDUCER)(undefined, {}),

    // A simplified list of searches that belong to the user, used on the dashboard
    isFetchingSearches: false,
    searches: null
};

export default function reducer (state:ISearchReducerState = defaultState, action:ISearchAction) {
    const { drawModule, geojson } = action;
    let features = drawModule?.drawControl?.getAll();
    switch (action.type) {
        case START_SEARCH_REQUEST:
            return {
                ...state,
                isSearching: true,
                search: null, // Looks cleaner clearing at moment, may be best to leave in case user cancels so previous results are retained?
                error: null
            };
        case START_SEARCH_SUCCESS:
            return {
                ...state,
                search: action.search,
                isSearchDirty: false,
                error: null
            };
        case START_SEARCH_FAILURE:
            return {
                ...state,
                isSearching: false,
                error: action.error
            };
        case FETCH_SEARCH_REQUEST:
            return {
                ...state,
                isFetching: true,
                isSearching: false,
                isPinging: false,
                search: null, // Looks cleaner clearing at moment, may be best to leave in case user cancels so previous results are retained?
                error: null
            };
        case FETCH_SEARCH_SUCCESS:
            return {
                ...state,
                isFetching: false,
                isSearching: ['ready', 'queued', 'running'].includes(action.search.status),
                search: action.search,
                isSearchDirty: false,
                areaType: 'drawn',
                searchArea: {
                    isDirty: false,
                    geojson: { geometry: action.search.area },
                    facetId: action.search.areaFacet.id,
                    facetValue: action.search.areaFacetValue,
                    facetValid: true,
                    criteria: action.search.criteria,
                    layerId: action.search.layerId,
                },
                error: null
            };
        case FETCH_SEARCH_FAILURE:
            return {
                ...state,
                isFetching: false,
                error: action.error
            };
        case PING_SEARCH_REQUEST:
            return {
                ...state,
                isPinging: true,
                error: null,
                searchArea: {
                    ...state.searchArea
                }
            };
        case PING_SEARCH_SUCCESS:
            return {
                ...state,
                isSearching: ['ready', 'queued', 'running'].includes(action.search.status),
                isPinging: false,
                search: action.search,
                error: (action.search.computedData && action.search.computedData.error) ? action.search.computedData.error : null,
                ...(action.search.status==='complete' && {searchArea: {
                    ...state.searchArea
                }})
            };
        case PING_SEARCH_FAILURE:
            return {
                ...state,
                isSearching: false,
                isPinging: false,
                error: action.error
            };
        case CANCEL_SEARCH_REQUEST:
            return {
                ...state,
                isCancelling: true,
                error: null
            };
        case CANCEL_SEARCH_SUCCESS:
            return {
                ...state,
                isSearching: false,
                isPinging: false,
                isCancelling: false,
                isSearchDirty: true,
                search: null,
                error: null
            };
        case CANCEL_SEARCH_FAILURE:
            return {
                ...state,
                isCancelling: false,
                error: action.error
            };

        case FETCH_SEARCHES_REQUEST:
            return {
                ...state,
                isFetchingSearches: true,
                error: null
            };
        case FETCH_SEARCHES_SUCCESS:
            return {
                ...state,
                isFetchingSearches: false,
                searches: action.searches,
                searchArea: {
                    ...state.searchArea,
                },
                error: null
            };
        case FETCH_SEARCHES_FAILURE:
            return {
                ...state,
                isFetchingSearches: false,
                error: action.error
            };
        case EXPORT_SEARCH_REQUEST:
            return {
                ...state,
                isExporting: true,
                error: null
            };
        case EXPORT_SEARCH_SUCCESS:
            return {
                ...state,
                isExporting: false,
                error: null
            };
        case EXPORT_SEARCH_FAILURE:
            return {
                ...state,
                isExporting: false,
                error: action.error
            };
        // When the project is changed then clear the searches
        case FETCH_CURRENT_PROJECT_SUCCESS:
        case SET_CURRENT_PROJECT_SUCCESS: {
            return {
                ...state,
                search: null,
                searches: []
            };
        }
        case SET_FORM_PROPERTIES:
        case SET_AREA_FACET:
        case SET_AREA_FACET_VALUE:
        case SET_AREA_GEOJSON:
        case SET_AREA_CRITERIA_ADVANCED_MODE:
        case SET_AREA_CRITERIA:
        case SET_AREA_LAYER:
            return action.reducerName === SEARCH_REDUCER ? {
                ...state,
                isSearchDirty: true,
                searchArea: areaReducer(SEARCH_REDUCER)(state.searchArea as SearchArea, action)
            } : state;
        case CLEAR_AREA:
            return action.reducerName === SEARCH_REDUCER ? {
                ...state,
                isSearchDirty: true,
                search: null,
                searchArea: areaReducer(SEARCH_REDUCER)(state.searchArea as SearchArea, action)
            } : state;
        case FETCH_LAYERS_SUCCESS:
            return {
                ...state,
                isSearchDirty: true,
                searchArea: areaReducer(SEARCH_REDUCER)(state.searchArea as SearchArea, action)
            };
        default:
            return state;
    }
}